import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Chip
} from '@mui/material';
import {
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';

interface Organization {
  id: string;
  name: string;
  slug: string;
}

const AdminStripeSync: React.FC = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<any>(null);

  useEffect(() => {
    checkPlatformAdminAccess();
  }, []);

  const checkPlatformAdminAccess = async () => {
    try {
      setCheckingAccess(true);
      const profile = await apiService.getProfile();
      if (profile.data?.user?.systemRole !== 'PLATFORM_ADMIN') {
        // Redirect to dashboard if not platform admin
        navigate('/dashboard');
        return;
      }
      // User is platform admin, proceed to fetch organizations
      fetchOrganizations();
    } catch (err: any) {
      setError('Failed to verify access. Please try again.');
      navigate('/dashboard');
    } finally {
      setCheckingAccess(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getOrganizations();
      // API returns { success, message, data: organizations[] }
      setOrganizations(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to fetch organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!selectedOrgId) {
      setError('Please select an organization');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setSyncResult(null);

      const result = await apiService.syncSubscriptionFromStripe(selectedOrgId);
      setSyncResult(result);
      setSuccess(result.message || 'Subscription synced successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.details || 'Failed to sync subscription from Stripe');
    } finally {
      setLoading(false);
    }
  };

  const selectedOrg = organizations.find(org => org.id === selectedOrgId);

  if (checkingAccess) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Stripe Sync
        </Typography>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Sync subscription data from Stripe to recover lost payment information and fix subscription state.
        This will update the subscription status, seats, billing periods, and customer information.
      </Typography>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }} 
          onClose={() => setError(null)}
          icon={<ErrorIcon />}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }} 
          onClose={() => setSuccess(null)}
          icon={<CheckCircleIcon />}
        >
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="flex-end">
            <Grid item xs={12} md={8}>
              <FormControl fullWidth>
                <InputLabel>Select Organization</InputLabel>
                <Select
                  value={selectedOrgId}
                  onChange={(e) => {
                    setSelectedOrgId(e.target.value);
                    setError(null);
                    setSuccess(null);
                    setSyncResult(null);
                  }}
                  label="Select Organization"
                  disabled={loading}
                >
                  <MenuItem value="">
                    <em>Select an organization...</em>
                  </MenuItem>
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name} ({org.slug})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} /> : <SyncIcon />}
                onClick={handleSync}
                disabled={!selectedOrgId || loading}
                sx={{ height: '56px' }}
              >
                {loading ? 'Syncing...' : 'Sync from Stripe'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {syncResult && syncResult.subscription && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Sync Results
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Organization
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedOrg?.name || 'Unknown'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={syncResult.subscription.status}
                  color={
                    syncResult.subscription.status === 'active' ? 'success' :
                    syncResult.subscription.status === 'canceled' ? 'error' :
                    syncResult.subscription.status === 'past_due' ? 'warning' : 'default'
                  }
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Seats Purchased
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {syncResult.subscription.seatsPurchased}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Seats Used
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {syncResult.subscription.seatsUsed}
                </Typography>
              </Grid>
              {syncResult.subscription.currentPeriodStart && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Current Period Start
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {new Date(syncResult.subscription.currentPeriodStart).toLocaleDateString()}
                  </Typography>
                </Grid>
              )}
              {syncResult.subscription.currentPeriodEnd && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Current Period End
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {new Date(syncResult.subscription.currentPeriodEnd).toLocaleDateString()}
                  </Typography>
                </Grid>
              )}
              {syncResult.subscription.paymentProviderId && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Stripe Subscription ID
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {syncResult.subscription.paymentProviderId}
                  </Typography>
                </Grid>
              )}
              {syncResult.subscription.customerId && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Stripe Customer ID
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {syncResult.subscription.customerId}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AdminStripeSync;

